import { useMemo, useState } from 'react'
import { ExternalLink, KeyRound, Search, Siren, UserCheck } from 'lucide-react'
import { GROEPEN, type Groep, type Meting, type Staat } from '../../gedeeld/types'
import { duur, geleden, ms, procent, STAAT_GEWICHT, STAAT_LABEL } from '../lib/opmaak'
import { Link, navigeer, useRoute, useTitel, zetParam } from '../lib/router'
import { useNu, useOverzicht } from '../lib/staat'
import { Foutvlak, Laden, Leeg, StaatIcoon, Teller } from './ui'

/* ===========================================================================
 *  Het overzicht
 *
 *  Van boven naar beneden de vragen in de volgorde waarin je ze stelt:
 *
 *    1. Is er iets stuk?             de balk bovenaan, en de tellers
 *    2. Wat precies, en sinds wann?  de open storingen
 *    3. En de rest?                  alle controles per groep, problemen
 *                                    eerst binnen elke groep
 *
 *  Meet de meter niet meer, dan staat dat bovenaan en NIET een groene balk
 *  met oude cijfers eronder. Dat is de ene leugen die deze pagina nooit mag
 *  vertellen.
 * =========================================================================== */

export default function Overzicht() {
  const { overzicht: o, fout, at: opgehaald } = useOverzicht()
  const nu = useNu()
  const { q } = useRoute()
  const groepFilter = (q.get('groep') as Groep | null) ?? null
  const staatFilter = (q.get('staat') as Staat | null) ?? null
  const [zoek, setZoek] = useState('')

  useTitel('Overzicht', o?.at ? `gemeten ${geleden(o.at, nu)}` : null)

  const perGroep = useMemo(() => {
    const m = new Map<Groep, Meting[]>()
    const z = zoek.trim().toLowerCase()
    for (const x of o?.metingen ?? []) {
      if (groepFilter && x.groep !== groepFilter) continue
      if (staatFilter && x.s !== staatFilter) continue
      if (z && !`${x.naam} ${x.id} ${x.u ?? ''}`.toLowerCase().includes(z)) continue
      const l = m.get(x.groep) ?? []
      l.push(x)
      m.set(x.groep, l)
    }
    for (const l of m.values()) l.sort((a, b) => STAAT_GEWICHT[a.s] - STAAT_GEWICHT[b.s] || a.naam.localeCompare(b.naam, 'nl'))
    return m
  }, [o, groepFilter, staatFilter, zoek])

  if (!o && fout) return <div className="pagina"><Foutvlak fout={fout} /></div>
  if (!o) return <Laden tekst="De laatste meting ophalen…" />

  const stil = !o.at || nu - o.at > o.stilNa
  const t = o.telling
  const totaal = t.werkt + t.traag + t.storing + t.onbekend
  const ontbreekt = [
    !o.tokens.github && 'GitHub', !o.tokens.cloudflare && 'Cloudflare', !o.tokens.supabase && 'de logboeken van Supabase',
    !o.tokens.melder && "de pc's (melder)",
  ].filter(Boolean) as string[]

  return (
    <div className="pagina">
      <Balk stil={stil} at={o.at} nu={nu} storing={t.storing} traag={t.traag} onbekend={t.onbekend} />

      {fout && opgehaald && (
        <Foutvlak fout={`Verversen lukt niet (${fout}). Wat je ziet is opgehaald ${geleden(opgehaald, nu)}.`} />
      )}

      <div className="tellers">
        <Teller s="alle" n={totaal} label="controles" actief={!staatFilter} onClick={() => zetParam('staat', null)} />
        <Teller s="storing" n={t.storing} label="storing" actief={staatFilter === 'storing'} onClick={() => zetParam('staat', staatFilter === 'storing' ? null : 'storing')} />
        <Teller s="traag" n={t.traag} label="traag of verstoord" actief={staatFilter === 'traag'} onClick={() => zetParam('staat', staatFilter === 'traag' ? null : 'traag')} />
        <Teller s="onbekend" n={t.onbekend} label="niet te meten" actief={staatFilter === 'onbekend'} onClick={() => zetParam('staat', staatFilter === 'onbekend' ? null : 'onbekend')} />
        <Teller s="werkt" n={t.werkt} label="werkt" actief={staatFilter === 'werkt'} onClick={() => zetParam('staat', staatFilter === 'werkt' ? null : 'werkt')} />
      </div>

      {o.storingen.length > 0 && !groepFilter && !staatFilter && (
        <section className="vlak storingen-vlak">
          <div className="vlak-kop">
            <h2><Siren size={16} /> Lopende storingen</h2>
            <Link naar="/storingen" className="vlak-link">Alle storingen</Link>
          </div>
          <ul className="storing-lijst">
            {o.storingen.slice(0, 8).map((s) => (
              <li key={s.id}>
                <Link naar={`/controle/${encodeURIComponent(s.controle)}`} className="storing-regel">
                  <StaatIcoon s="storing" />
                  <span className="storing-naam">{s.naam}</span>
                  <span className="storing-uitleg afkap">{s.laatste ?? s.uitleg}</span>
                  <span className="storing-duur" title={`Sinds ${new Date(s.begon).toLocaleString('nl-NL')}`}>{duur(nu - s.begon)}</span>
                  {s.erkendDoor && <span className="erkend" title={s.notitie ?? undefined}><UserCheck size={13} /> {s.erkendDoor}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {ontbreekt.length > 0 && !groepFilter && (
        <Link naar="/configuratie" className="tip-regel">
          <KeyRound size={15} />
          <span>Nog niet ingesteld: {ontbreekt.join(', ')}. Zonder die tokens staan een paar controles op "onbekend". Hoe je ze zet staat op Configuratie.</span>
        </Link>
      )}

      <div className="werkbalk">
        <div className="zoekveld">
          <Search size={15} />
          <input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek een controle…" />
        </div>
        <button type="button" className={`filter-pil ${!groepFilter ? 'actief' : ''}`} onClick={() => zetParam('groep', null)}>Alles</button>
        {GROEPEN.filter((g) => (o.metingen ?? []).some((m) => m.groep === g.key)).map((g) => {
          const rood = o.metingen.filter((m) => m.groep === g.key && m.s === 'storing').length
          return (
            <button key={g.key} type="button" className={`filter-pil ${groepFilter === g.key ? 'actief' : ''}`}
              onClick={() => zetParam('groep', groepFilter === g.key ? null : g.key)}>
              {g.naam}{rood ? <span className="pil-rood"> · {rood}</span> : null}
            </button>
          )
        })}
      </div>

      {GROEPEN.filter((g) => perGroep.has(g.key)).map((g) => {
        const lijst = perGroep.get(g.key)!
        return (
          <section key={g.key} className="groep-vak">
            <div className="groep-vak-kop">
              <h2>{g.naam}</h2>
              <MiniTelling lijst={lijst} />
              <p>{g.uitleg}</p>
            </div>
            <div className="tegel-raster">
              {lijst.map((m) => <Tegel key={m.id} m={m} beschikbaar={o.beschikbaar[m.id]} nu={nu} />)}
            </div>
          </section>
        )
      })}

      {!perGroep.size && (
        <Leeg titel="Niets gevonden" tekst={zoek ? `Geen controle past bij "${zoek}".` : 'Geen controles in deze selectie.'} />
      )}
    </div>
  )
}

function Balk({ stil, at, nu, storing, traag, onbekend }: { stil: boolean; at: number | null; nu: number; storing: number; traag: number; onbekend: number }) {
  if (stil) {
    return (
      <div className="balk s-onbekend" role="alert">
        <StaatIcoon s="onbekend" grootte={22} />
        <div>
          <strong>De meter staat stil</strong>
          <p>{at ? `De laatste ronde was ${geleden(at, nu)}. Wat hieronder staat is die laatste stand, niet de huidige.` : 'Er is nog niets gemeten.'} De ronde draait elke minuut als cron-trigger van de Worker; kijk in het logboek van truckwash-health bij Cloudflare.</p>
        </div>
      </div>
    )
  }
  const s: Staat = storing ? 'storing' : traag ? 'traag' : 'werkt'
  return (
    <div className={`balk s-${s}`} role={storing ? 'alert' : 'status'}>
      <StaatIcoon s={s} grootte={22} />
      <div>
        <strong>{storing ? `${storing} storing${storing === 1 ? '' : 'en'}` : traag ? 'Alles werkt, op een paar dingen na' : 'Alles werkt'}</strong>
        <p>
          {storing ? 'Iets doet het niet. De lopende storingen staan hieronder, met sinds wanneer en wat er gemeten is.' : traag ? `${traag} controle(s) traag of half.` : 'Elke controle gaf bij de laatste ronde een goed antwoord.'}
          {onbekend ? ` ${onbekend} controle(s) konden niet meten.` : ''} Laatste ronde {geleden(at, nu)}.
        </p>
      </div>
    </div>
  )
}

function MiniTelling({ lijst }: { lijst: Meting[] }) {
  const t = { storing: 0, traag: 0, onbekend: 0, werkt: 0 } as Record<Staat, number>
  for (const m of lijst) t[m.s]++
  return (
    <span className="mini-telling">
      {(['storing', 'traag', 'onbekend', 'werkt'] as Staat[]).filter((s) => t[s]).map((s) => (
        <span key={s} className={`s-${s}`} title={STAAT_LABEL[s]}><StaatIcoon s={s} grootte={13} /> {t[s]}</span>
      ))}
    </span>
  )
}

function Tegel({ m, beschikbaar, nu }: { m: Meting; beschikbaar: number | null | undefined; nu: number }) {
  const oud = m.elke > 1 && nu - m.at > 90_000
  return (
    <div className={`tegel s-${m.s}`} role="link" tabIndex={0}
      onClick={() => navigeer(`/controle/${encodeURIComponent(m.id)}`)}
      onKeyDown={(e) => { if (e.key === 'Enter') navigeer(`/controle/${encodeURIComponent(m.id)}`) }}>
      <div className="tegel-kop">
        <StaatIcoon s={m.s} />
        <span className="tegel-naam">{m.naam}</span>
        {m.link && (
          <a href={m.link} target="_blank" rel="noopener noreferrer" className="tegel-uit" title="Openen" onClick={(e) => e.stopPropagation()}>
            <ExternalLink size={13} />
          </a>
        )}
      </div>
      <p className="tegel-uitleg">{m.u ?? (m.s === 'werkt' ? 'Goed antwoord.' : STAAT_LABEL[m.s])}</p>
      <div className="tegel-voet">
        <span className="sr-only">{STAAT_LABEL[m.s]}.</span>
        {typeof m.ms === 'number' && <span title="Hoe lang de meting duurde">{ms(m.ms)}</span>}
        {beschikbaar !== undefined && beschikbaar !== null && <span title="Beschikbaar in de afgelopen 24 uur">{procent(beschikbaar, 1)}</span>}
        <span title={`In deze stand sinds ${new Date(m.sinds).toLocaleString('nl-NL')}`}>{m.s === 'werkt' ? 'goed' : STAAT_LABEL[m.s].toLowerCase()} sinds {duur(nu - m.sinds)}</span>
        {oud && <span title="Deze controle meet niet elke minuut">gemeten {geleden(m.at, nu)}</span>}
      </div>
    </div>
  )
}
